/**
 * A select, restyled from Radix.
 *
 * Radix rather than a native `<select>` because the filter bars need option
 * content a native select cannot hold — a stage badge with its colour dot, for
 * instance — while keeping the keyboard behaviour people expect: type-ahead,
 * arrow navigation, Home/End, Escape to close.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.6
 */
import * as RadixSelect from '@radix-ui/react-select';

import { cn } from '../../lib/cn';

import { Icon } from './Icon';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  value: string | undefined;
  onValueChange: (value: string) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  /** Accessible name, for a select with no visible label beside it. */
  ariaLabel?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}

export function Select({
  value,
  onValueChange,
  options,
  placeholder,
  ariaLabel,
  id,
  disabled = false,
  className,
}: SelectProps): React.JSX.Element {
  /* `value` is spread conditionally rather than passed: Radix types it as
     optional-without-undefined, and an unset filter genuinely has no value, so
     passing `undefined` explicitly is what `exactOptionalPropertyTypes`
     rejects. Omitting the prop also leaves the select genuinely empty rather
     than controlled-to-nothing. */
  return (
    <RadixSelect.Root
      {...(value === undefined ? {} : { value })}
      onValueChange={onValueChange}
      disabled={disabled}
    >
      <RadixSelect.Trigger
        id={id}
        aria-label={ariaLabel}
        className={cn(
          'border-gray-200 text-navy-text flex min-h-11 w-full items-center justify-between',
          'gap-2 rounded-md border bg-white px-3 text-sm',
          'disabled:bg-gray-100 disabled:opacity-60',
          className,
        )}
      >
        <RadixSelect.Value placeholder={placeholder} />
        {/* The sprite's arrow points right; rotated to point down, which is
            what a select's disclosure affordance means. */}
        <RadixSelect.Icon className="rotate-90">
          <Icon name="arrow" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>

      <RadixSelect.Portal>
        <RadixSelect.Content
          /* `position="popper"` so the list opens beside the trigger rather
             than over it, which would hide the current value while choosing. */
          position="popper"
          sideOffset={4}
          className={cn(
            'border-gray-100 z-50 overflow-hidden rounded-md border bg-white shadow-md',
            /* Matched to the trigger's width: a narrower panel reads as
               detached from the control that opened it. */
            'w-[var(--radix-select-trigger-width)]',
          )}
        >
          <RadixSelect.Viewport className="p-1">
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value}
                value={option.value}
                className={cn(
                  'text-navy-text flex min-h-11 cursor-pointer items-center rounded-md px-3',
                  'text-sm outline-none',
                  'data-[highlighted]:bg-gray-100',
                  'data-[state=checked]:bg-blue-light data-[state=checked]:text-blue-strong',
                )}
              >
                <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
